import RelativeLink from "common/components/RelativeLink";
import { Button } from "common/components/ui/Button";
import {
  DialogClose,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerClose,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import type { JSX } from "react";

import SeatPurchaseImage from "assets/images/seat-purchase.png";

interface SeatPurchaseProps {
  additionalSeatsCount: number;
  goToManageSubscription: () => void;
}

export default function SeatPurchase({
  additionalSeatsCount,
  goToManageSubscription,
}: SeatPurchaseProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { subscription_status, agency_subscription_status, role, permissions } =
    useSelectedWorkspaceContext();

  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  const hasAgencySubscription =
    agency_subscription_status && agency_subscription_status !== "cancelled";
  const isFreeTrial = subscription_status === "in_trial";
  const isMultiple = additionalSeatsCount > 1;

  let title: string;
  let description: string;
  let imgSrc: string;
  let mainActionComponent: JSX.Element;
  let secondaryActionComponent: JSX.Element;

  if (hasAgencySubscription) {
    title = "No seats left on this workspace";
    description = "All seats on this workspace are currently in use. ";

    if (role === "owner") {
      description +=
        "You can manage your currently assigned seats in the accounts page, or allocate more seats from your global agency pool";

      mainActionComponent = (
        <Button asChild>
          <RelativeLink to="settings/billing">Allocate seats</RelativeLink>
        </Button>
      );
      secondaryActionComponent = (
        <Close asChild>
          <Button asChild variant="secondary-black">
            <RelativeLink to="accounts">Go to accounts</RelativeLink>
          </Button>
        </Close>
      );
    } else {
      description += "Contact an administrator to resolve";

      secondaryActionComponent = (
        <Close asChild>
          <Button variant="secondary-black">Close</Button>
        </Close>
      );
    }
  } else {
    const hasPermissions = permissions.includes("billing_full");

    secondaryActionComponent = (
      <Close asChild>
        <Button variant="secondary-black">Cancel</Button>
      </Close>
    );
    imgSrc = SeatPurchaseImage;

    let buttonText: string;

    if (isFreeTrial) {
      title = "Add a trial seat to use LinkedIn Outreach";
      description = "This feature requires a LinkedIn Outreach plan. ";

      if (hasPermissions) {
        description +=
          "You will not be charged for any seats until your trial expires";
        buttonText = "Add a seat";
      }
    } else if (isMultiple) {
      title = "Not enough seats";
      description =
        "You do not have enough unassigned seats for all selected accounts. ";

      if (hasPermissions) {
        description += `You must purchase ${additionalSeatsCount} more seats to complete this action`;
        buttonText = "Get more seats";
      }
    } else {
      title = "You need to purchase a seat to use LinkedIn Outreach";
      description = "You do not have available seats at this time. ";

      if (hasPermissions) {
        description += "You will need to purchase a seat to use this feature";
        buttonText = "Purchase a seat";
      }
    }

    if (hasPermissions) {
      mainActionComponent = (
        <Button variant="gradient" onClick={goToManageSubscription}>
          {buttonText}
        </Button>
      );
    } else {
      description += "Contact an administrator to resolve";
    }
  }

  return (
    <>
      <Header title={title} subtitle={description} imgSrc={imgSrc} />
      <Footer className="justify-between">
        {secondaryActionComponent}
        {mainActionComponent}
      </Footer>
    </>
  );
}
